/**
 * 🎬 영상 제작(M&E) 사이징 — 엑셀 「M&E」 시트 + 「M&E Database」 (v2.233.0)
 *   영상 요구(읽기=재생 · 쓰기=입력 각각 프레임 크기·fps·압축·스트림 수) → 처리량(MB/s)
 *   + 용량 요구(사용 가능 TB · 디스크 크기 · RAID · 스페어) → 제품군별 필요한 디스크 수 → 모델 + JBOD 추천 표 13줄
 */
import { useState } from 'react';
import { calcMediaEnt } from '../../lib/sizing/calc';
import type { MeInput, MeVideo } from '../../lib/sizing/calc';
import {
  DRIVE_SIZES_TB, ME_CODECS, ME_CODEC_LABEL, ME_FRAME_LABEL, ME_FRAME_RATES, ME_FRAME_SIZES, ME_STREAM_REF, ME_THROUGHPUT_REF, usableTbPerDrive,
} from '../../lib/sizing/data';
import { Dash, Field, Formula, NumInput, Panel, SelectInput, SimpleTable, Stat, StatGrid, TRIO_GRID, TRIO_RESULT, fmt } from './SizingUi';

const DEFAULT_VIDEO: MeVideo = { frame: '(2K) 2048 x 1556', fps: '24fps', codec: '10bit RGB (Uncompressed)', streams: 1 };

function VideoFields({ v, onChange, title }: { v: MeVideo; onChange: (next: MeVideo) => void; title: string }) {
  return (
    <div className="mb-3">
      <div className="text-sm font-semibold text-slate-700 mb-1 pb-1 border-b border-emerald-200/70">{title}</div>
      <Field label="프레임 크기"><SelectInput value={v.frame} options={ME_FRAME_SIZES.map((f) => ({ value: f, label: ME_FRAME_LABEL[f] }))} onChange={(frame) => onChange({ ...v, frame })} /></Field>
      <Field label="프레임 레이트"><SelectInput value={v.fps} options={ME_FRAME_RATES.map((f) => ({ value: f, label: f }))} onChange={(fps) => onChange({ ...v, fps })} /></Field>
      <Field label="압축"><SelectInput value={v.codec} options={ME_CODECS.map((c) => ({ value: c, label: ME_CODEC_LABEL[c] }))} onChange={(codec) => onChange({ ...v, codec })} /></Field>
      <Field label="스트림 수"><NumInput value={v.streams} min={0} step={1} onChange={(streams) => onChange({ ...v, streams })} suffix="개" /></Field>
    </div>
  );
}

export function MediaCalc() {
  const [i, setI] = useState<MeInput>({ read: DEFAULT_VIDEO, write: DEFAULT_VIDEO, usableTb: 30, driveTb: 8, drivePrice: 0, raid: 'RAID6', spare: 1, series: 'highIops' });
  const r = calcMediaEnt(i);
  const familyLabel = { DS3000: 'EonStor DS 3000 (T)', DS4000: 'EonStor DS 4000 Gen2' } as const;
  const famRows = (['DS4000', 'DS3000'] as const).map((f) => {
    const n = r.needed[f];
    const cell = (x: number | null) => (x === null ? <span className="text-red-600">최대 초과</span> : fmt(x));
    return [familyLabel[f], cell(n.fromRead), cell(n.fromWrite), fmt(n.fromCapacity), <b key="t" className="text-base">{n.total === null ? <span className="text-red-600">안 됨</span> : `${fmt(n.total)}개`}</b>];
  });

  return (
    <div className="space-y-6">
      {/* 넓은 PC 세 상자 나란히 · 중간 폭(lg)은 입력 둘 나란히 + 결과는 아래 한 줄 · 폰은 세로 (TRIO_GRID) */}
      <div className={TRIO_GRID}>
        <Panel title="영상 성능 요구" tone="input" note="처리량 표는 100% 순차 읽기 또는 100% 순차 쓰기 기준이다(섞인 I/O 가 아니다).">
          <VideoFields title="읽기 — 재생(playback) 스트림" v={i.read} onChange={(read) => setI({ ...i, read })} />
          <VideoFields title="쓰기 — 입력(ingest) 스트림" v={i.write} onChange={(write) => setI({ ...i, write })} />
        </Panel>
        <Panel title="용량 요구 · 구성" tone="input">
          <Field label="사용 가능 용량"><NumInput value={i.usableTb} min={0} step={1} onChange={(usableTb) => setI({ ...i, usableTb })} suffix="TB" /></Field>
          <Field label="디스크 크기" hint={`실제 ${fmt(usableTbPerDrive(i.driveTb), 3)}TB 씩 쓸 수 있음`}><SelectInput value={i.driveTb} options={DRIVE_SIZES_TB.map((t) => ({ value: t, label: `${t} TB` }))} onChange={(driveTb) => setI({ ...i, driveTb })} /></Field>
          <Field label="디스크 1개 가격" hint="정가 합계에 더한다 · 모르면 0"><NumInput value={i.drivePrice} min={0} step={1} onChange={(drivePrice) => setI({ ...i, drivePrice })} suffix="USD" /></Field>
          <Field label="RAID 종류"><SelectInput value={i.raid} options={[{ value: 'RAID6', label: 'RAID 6' }, { value: 'RAID5', label: 'RAID 5' }]} onChange={(raid) => setI({ ...i, raid })} /></Field>
          <Field label="인클로저(서랍)당 스페어"><SelectInput value={i.spare} options={[{ value: 0, label: '0개' }, { value: 1, label: '1개' }]} onChange={(spare) => setI({ ...i, spare })} /></Field>
          <Field label="모델 종류" hint="정가에만 영향"><SelectInput value={i.series} options={[{ value: 'highIops', label: 'High IOPS 모델 (T · U)' }, { value: 'standard', label: '표준 모델' }]} onChange={(series) => setI({ ...i, series })} /></Field>
        </Panel>
        <Panel title="처리량 · 필요한 디스크 수" tone="result" className={TRIO_RESULT}>
          <StatGrid className="max-w-3xl">
            <Stat label="읽기(재생) 처리량" value={fmt(r.readMbps, 1)} unit="MB/s" big />
            <Stat label="쓰기(입력) 처리량" value={fmt(r.writeMbps, 1)} unit="MB/s" big />
          </StatGrid>
          {/* 결과 상자가 한 줄을 다 차지할 때(lg) 표가 화면 끝까지 늘어나 숫자가 멀어지지 않게 폭을 잡는다 */}
          <div className="mt-3 max-w-3xl">
            <SimpleTable wrap head={['제품군', '읽기용', '쓰기용', '용량용', '필요 디스크']} align={['l', 'r', 'r', 'r', 'r']} rows={famRows} />
          </div>
          <Formula>
            <p>처리량 = 표 값(프레임 크기·압축·fps 당 MB/s) × 스트림 수</p>
            <p>읽기용 디스크 = 올림(읽기 처리량 ÷ 제품군·프레임별 디스크 1개 읽기 처리량) — 제품군 최대를 넘으면 「최대 초과」 (5K·4K 4096×3112 는 DS 3000 으로 안 됨)</p>
            <p>용량용 디스크 = 올림(사용 가능 TB ÷ 디스크 1개 실제 TB) · 필요 디스크 = 셋 중 가장 큰 값</p>
          </Formula>
        </Panel>
      </div>

      <Panel title="추천 구성 — 모델 + JBOD 별 (엑셀 표 그대로 13줄)" tone="result" note="정가(MSRP · USD)는 2015~2017년 당시 값이며 High IOPS 모델·JBOD·FC 16Gb 호스트 보드(DS 3000 2장 · DS 4000 4장)·16GB 메모리 차액을 더한 것이다. 「—」는 처리량 최대 초과 또는 JBOD 연결 한도 초과로 그 구성으로는 안 되는 경우.">
        <SimpleTable
          head={['모델', 'JBOD', '권장 구성', '총 디스크', '사용 가능(TB)', '스페어', '높이(U)', '정가 합계($)', '$ / MB/s', '$ / TB']}
          align={['l', 'l', 'c', 'r', 'r', 'r', 'r', 'r', 'r', 'r']}
          rows={r.rows.map((x) => [
            x.system, x.jbod,
            x.config ?? <Dash />, x.totalDrives === null ? <Dash /> : fmt(x.totalDrives), x.usableTb === null ? <Dash /> : fmt(x.usableTb, 1),
            x.spares === null ? <Dash /> : fmt(x.spares), x.heightU === null ? <Dash /> : fmt(x.heightU),
            x.msrp === null ? (x.msrpNote ?? <Dash />) : fmt(x.msrp), x.msrpPerMbps === null ? <Dash /> : fmt(x.msrpPerMbps, 1), x.msrpPerTb === null ? <Dash /> : fmt(x.msrpPerTb, 1),
          ])}
        />
        <Formula>
          <p>데이터 디스크 = 인클로저(또는 서랍)마다 베이 수 − 패리티(RAID 6 은 2 · RAID 5 는 1) − 스페어</p>
          <p>JBOD 수 = 올림((필요 디스크 − RAID 인클로저의 데이터 디스크) ÷ JBOD 1대(또는 서랍 1개)의 데이터 디스크) · 48·60베이 JBOD 는 서랍 3개가 1대</p>
          <p>사용 가능(TB) = (데이터 디스크 수) × 디스크 1개 실제 TB · 48·60베이 RAID(3048RE · 3060RE)는 필요한 만큼의 서랍만 채운다</p>
          <p>정가 합계 = 모델 정가 + 메모리 차액 + JBOD 정가 × 대수 + 디스크 가격 × 총 디스크 + 호스트 보드 · $/MB/s = 정가 ÷ 읽기 처리량 · $/TB = 정가 ÷ 사용 가능 TB</p>
        </Formula>
      </Panel>

      <details className="text-sm">
        <summary className="cursor-pointer text-slate-600 hover:text-slate-800">📎 참고 — 모델별 동시 스트림 수 · 최대 처리량 (엑셀 오른쪽 표)</summary>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
          <Panel title="동시 스트림 수 (읽기 / 쓰기)">
            <SimpleTable head={['영상', 'DS 4000 Gen2', 'DS 3000 (T)']} align={['l', 'c', 'c']} rows={ME_STREAM_REF.map((x) => [x.frame, `${x.ds4000[0]} / ${x.ds4000[1]}`, x.ds3000 ? `${x.ds3000[0]} / ${x.ds3000[1]}` : 'DS 4000 권장'])} />
          </Panel>
          <Panel title="최대 처리량 MB/s (읽기 / 쓰기)">
            <SimpleTable head={['영상', 'DS 4000 Gen2', 'DS 3000 (T)']} align={['l', 'c', 'c']} rows={ME_THROUGHPUT_REF.map((x) => [x.frame, `${fmt(x.ds4000[0])} / ${fmt(x.ds4000[1])}`, x.ds3000 ? `${fmt(x.ds3000[0])} / ${fmt(x.ds3000[1])}` : 'DS 4000 권장'])} />
          </Panel>
        </div>
      </details>
    </div>
  );
}
